/**
 * Enrich orders with live OrderDesk checkout_data.Dispatch / Delivery.
 * Same method as Admin Dashboard: Orderdesker
 *   GET /orders?folder=DD-MM-YYYY&dateField=delivery|dispatch&limit=500
 * Row col 15 = Dispatch, col 16 = Delivery (user-set in OrderDesk).
 *
 * Firebase is not the source of truth for these fields.
 */
import { odFetch } from '../services/orderDeskAdminAPI';
import { formatDDMMYYYY, parseDDMMYYYY, toOrderDeskCheckoutDate } from '../services/dateService';
import { getOrderDeliveryDate, getOrderDispatchDate, normalizeOrderDate } from './orderDateUtils';

/**
 * @param {string} folderDdMmYyyy - OrderDesk folder / date filter DD-MM-YYYY
 * @param {'delivery'|'dispatch'} [dateField='delivery']
 * @returns {Promise<Map<string, { dispatch: string, delivery: string }>>}
 */
export async function fetchOrderDeskCheckoutDatesByFolder(folderDdMmYyyy, dateField = 'delivery') {
  const folder = String(folderDdMmYyyy || '').trim();
  if (!/^\d{2}-\d{2}-\d{4}$/.test(folder)) {
    return new Map();
  }
  const field = dateField === 'dispatch' ? 'dispatch' : 'delivery';
  const res = await odFetch(
    `/orders?folder=${encodeURIComponent(folder)}&dateField=${field}&limit=500`,
  );
  const rows = res?.values || [];
  const odMap = new Map();
  rows.forEach((row) => {
    if (!Array.isArray(row) || row.length < 17) return;
    const orderId = row[0];
    if (!orderId) return;
    odMap.set(String(orderId).trim(), {
      dispatch: row[15] || '',
      delivery: row[16] || '',
    });
  });
  return odMap;
}

/**
 * Resolve a date-like value to OrderDesk folder form DD-MM-YYYY.
 * @param {*} value
 * @returns {string|null}
 */
export function toOrderDeskFolderDate(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) return trimmed;
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      const d = new Date(`${trimmed.slice(0, 10)}T12:00:00`);
      if (!Number.isNaN(d.getTime())) return formatDDMMYYYY(d);
    }
    const parsed = parseDDMMYYYY(trimmed);
    if (parsed) return formatDDMMYYYY(parsed);
  }
  const asCheckout = toOrderDeskCheckoutDate(value);
  return asCheckout && /^\d{2}-\d{2}-\d{4}$/.test(asCheckout) ? asCheckout : null;
}

function orderLookupKeys(order) {
  return [
    order?.source_id,
    order?.sourceId,
    order?.orderNumber,
    order?.order_number,
    order?.['Source ID#'],
    order?.orderId,
    order?.id,
    order?.['Order ID'],
  ]
    .map((v) => String(v ?? '').trim())
    .filter(Boolean);
}

/**
 * Apply an OrderDesk date map onto order objects (checkout_data + convenience fields).
 * @param {Array<object>} orders
 * @param {Map<string, { dispatch: string, delivery: string }>} odMap
 * @returns {Array<object>}
 */
export function applyOrderDeskCheckoutDates(orders, odMap) {
  if (!Array.isArray(orders) || !odMap || odMap.size === 0) return orders || [];
  return orders.map((o) => {
    let live = null;
    for (const key of orderLookupKeys(o)) {
      if (odMap.has(key)) {
        live = odMap.get(key);
        break;
      }
    }
    if (!live || (!live.dispatch && !live.delivery)) return o;

    const dispatchRaw = live.dispatch || '';
    const deliveryRaw = live.delivery || '';
    const prevCd =
      o.checkout_data && typeof o.checkout_data === 'object' && !Array.isArray(o.checkout_data)
        ? o.checkout_data
        : {};
    const checkout_data = {
      ...prevCd,
      ...(deliveryRaw ? { Delivery: toOrderDeskCheckoutDate(deliveryRaw) || deliveryRaw } : {}),
      ...(dispatchRaw ? { Dispatch: toOrderDeskCheckoutDate(dispatchRaw) || dispatchRaw } : {}),
    };

    const next = {
      ...o,
      checkout_data,
    };
    if (dispatchRaw) {
      next.dispatch_date = toOrderDeskCheckoutDate(dispatchRaw) || dispatchRaw;
      next.dispatchDate = normalizeOrderDate(dispatchRaw) || next.dispatchDate;
    }
    if (deliveryRaw) {
      next.delivery_date = toOrderDeskCheckoutDate(deliveryRaw) || deliveryRaw;
      next.deliveryDate = normalizeOrderDate(deliveryRaw) || next.deliveryDate;
    }
    return next;
  });
}

/**
 * Collect unique folder dates from orders (delivery first, then dispatch).
 * @param {Array<object>} orders
 * @param {'delivery'|'dispatch'|'both'} [prefer='both']
 * @returns {Array<{ folder: string, dateField: 'delivery'|'dispatch' }>}
 */
export function collectOrderDeskFolderQueries(orders, prefer = 'both') {
  const seen = new Set();
  const queries = [];
  const push = (raw, dateField) => {
    const folder = toOrderDeskFolderDate(raw);
    if (!folder) return;
    const key = `${folder}|${dateField}`;
    if (seen.has(key)) return;
    seen.add(key);
    queries.push({ folder, dateField });
  };

  (orders || []).forEach((o) => {
    if (prefer === 'dispatch' || prefer === 'both') {
      push(getOrderDispatchDate(o), 'dispatch');
    }
    if (prefer === 'delivery' || prefer === 'both') {
      push(getOrderDeliveryDate(o), 'delivery');
    }
  });
  return queries;
}

/**
 * Enrich orders with live OrderDesk Dispatch/Delivery (Admin Dashboard method).
 * Fetches once per unique folder date found on the orders (or a forced filter date).
 *
 * @param {Array<object>} orders
 * @param {{
 *   filterDate?: string, // YYYY-MM-DD or DD-MM-YYYY
 *   dateField?: 'delivery'|'dispatch',
 *   prefer?: 'delivery'|'dispatch'|'both',
 * }} [opts]
 * @returns {Promise<Array<object>>}
 */
export async function enrichOrdersWithOrderDeskCheckoutDates(orders, opts = {}) {
  const list = Array.isArray(orders) ? orders : [];
  if (list.length === 0) return list;

  const merged = new Map();
  const dateField = opts.dateField === 'dispatch' ? 'dispatch' : 'delivery';

  if (opts.filterDate) {
    const folder = toOrderDeskFolderDate(opts.filterDate);
    if (folder) {
      try {
        const map = await fetchOrderDeskCheckoutDatesByFolder(folder, dateField);
        map.forEach((v, k) => merged.set(k, v));
      } catch (err) {
        console.warn('[enrichOrdersWithOrderDeskCheckoutDates] folder enrich failed:', err);
      }
    }
  } else {
    const queries = collectOrderDeskFolderQueries(list, opts.prefer || 'both');
    await Promise.all(
      queries.map(async ({ folder, dateField: field }) => {
        try {
          const map = await fetchOrderDeskCheckoutDatesByFolder(folder, field);
          map.forEach((v, k) => {
            const prev = merged.get(k) || { dispatch: '', delivery: '' };
            merged.set(k, {
              dispatch: v.dispatch || prev.dispatch,
              delivery: v.delivery || prev.delivery,
            });
          });
        } catch (err) {
          console.warn('[enrichOrdersWithOrderDeskCheckoutDates] folder enrich failed:', folder, err);
        }
      }),
    );
  }

  if (merged.size === 0) return list;
  return applyOrderDeskCheckoutDates(list, merged);
}
